import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Divider,
  Toolbar,
} from '@mui/material';
import { 
  Send, 
  Close, 
  Attachment,
  Subject,
  Email as EmailIcon,
} from '@mui/icons-material';

import { SysEditor } from '../syseditor';
import { emailService } from '../../services/emailService';

interface EnviarEmailFormProps {
  open: boolean;
  onClose: () => void;
  destinatario: string;
  destinatarioNome?: string;
  loading?: boolean;
}

const EnviarEmailForm: React.FC<EnviarEmailFormProps> = ({
  open,
  onClose,
  destinatario,
  destinatarioNome,
  loading: externalLoading = false,
}) => {
  const [assunto, setAssunto] = useState('');
  const [corpo, setCorpo] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      // Reset form quando abrir
      setAssunto('');
      setCorpo('');
      setError(null);
      setSuccess(null);
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!assunto.trim()) {
      setError('Digite um assunto para o email');
      return;
    }

    if (!corpo.trim()) {
      setError('Digite o conteúdo do email');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const emailData = {
        to: destinatario,
        subject: assunto.trim(),
        html: corpo,
      };

      const result = await emailService.sendEmail(emailData);
      
      if (result.success) {
        setSuccess('Email enviado com sucesso!');
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setError(result.message || 'Erro ao enviar email');
      }
    } catch (err: any) {
      console.error('Erro ao enviar email:', err);
      setError(err.message || 'Erro ao enviar email');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading && !externalLoading) {
      onClose();
    }
  };

  const handleCorpoChange = (content: string) => {
    setCorpo(content);
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh', maxHeight: '90vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <EmailIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">
              Enviar Email
            </Typography>
          </Box>
          <IconButton 
            onClick={handleClose}
            disabled={loading || externalLoading}
            size="small"
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {/* Destinatário */}
          <Box mb={2}>
            <Typography variant="subtitle2" gutterBottom>
              Destinatário
            </Typography>
            <Box 
              sx={{ 
                p: 1.5, 
                bgcolor: 'grey.50', 
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.300'
              }}
            >
              <Typography variant="body2" color="text.primary">
                <strong>{destinatarioNome || 'Email'}:</strong> {destinatario}
              </Typography>
            </Box>
          </Box>

          {/* Assunto */}
          <Box mb={2}>
            <TextField
              fullWidth
              label="Assunto"
              value={assunto}
              onChange={(e) => setAssunto(e.target.value)}
              disabled={loading || externalLoading}
              required
              InputProps={{
                startAdornment: <Subject sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              placeholder="Digite o assunto do email..."
            />
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Editor de Conteúdo */}
          <Box mb={2}>
            <Typography variant="subtitle2" gutterBottom>
              Conteúdo do Email
            </Typography>
            <Box sx={{ border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}>
              <SysEditor
                value={corpo}
                onChange={handleCorpoChange}
                placeholder="Digite o conteúdo do seu email aqui..."
                height={300}
                disabled={loading || externalLoading}
                showVariables={false}
              />
            </Box>
          </Box>

          {/* Informações adicionais */}
          <Box sx={{ p: 1, bgcolor: 'info.50', borderRadius: 1, border: '1px solid', borderColor: 'info.200' }}>
            <Typography variant="caption" color="info.main">
              💡 <strong>Dica:</strong> Use o editor para formatar o texto, inserir imagens, tabelas e criar emails profissionais.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handleClose}
            disabled={loading || externalLoading}
            variant="outlined"
          >
            Cancelar
          </Button>
          
          <Button
            type="submit"
            variant="contained"
            disabled={loading || externalLoading || !assunto.trim() || !corpo.trim()}
            startIcon={loading ? <CircularProgress size={20} /> : <Send />}
          >
            {loading ? 'Enviando...' : 'Enviar Email'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EnviarEmailForm; 